import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Button, Steps, Typography, Divider, Alert, Spin } from 'antd';
import { CheckOutlined, HeartFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import PatientService from '../../services/PatientService';
import { GlobalSelectStyles as SharedGlobalSelectStyles } from '../shared/StyledSelect';
import LanguageToggle from '../shared/LanguageToggle';
import { LanguageProvider, useLanguage } from '../../contexts/LanguageContext';
import { createT } from '../../locales';
import { getValidationSchema } from './validationSchemas';
import { StepPatientInfo, StepDemographics, StepMedicalHistory, StepConsents } from './steps';
import useFormConfig from './useFormConfig';
import {
  WizardContainer,
  WizardHeader,
  HeaderRow,
  StepContainer,
  NavigationButtons,
  StyledSteps,
  StyledButton,
  FormProgress,
  MainTitle,
  inlineStyles,
  GlobalButtonStyles
} from './styled';

const { Step } = Steps;
const { Title, Paragraph } = Typography;

// Re-export formatPhoneNumber for any external consumers
export { formatPhoneNumber } from './constants';

const BusDataFormWizard = () => {
  console.log('=== BusDataFormWizard component started rendering ===');
  
  // Language/translation support
  const { language } = useLanguage();
  const t = createT(language, 'screening');
  
  // Basic state logging
  console.log('Component initialization started...');
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // Patient checking states
  const [patientCheckStatus, setPatientCheckStatus] = useState(null); // null, 'checking', 'found', 'not_found', 'duplicates'
  const [existingPatientInfo, setExistingPatientInfo] = useState(null);
  const [checkingPatient, setCheckingPatient] = useState(false);

  // Debounce timer for patient check
  const debounceTimerRef = useRef(null);

  // FormConfig: per-event field visibility
  const { loadConfig, isFieldVisible, isFieldRequired, isSectionVisible } = useFormConfig();

  // Handle patient context from URL parameters (when coming from screening entry)
  const location = useLocation();
  const navigate = useNavigate();
  const [patientContext, setPatientContext] = useState(null);
  const [loadingPatientContext, setLoadingPatientContext] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Patient Info
    usedBefore: '',
    testingCode: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    selectedEvent: undefined, // For returning users - undefined to show placeholder

    // Step 2: Contact & Demographics
    phoneNumber: '',
    emailAddress: '',
    stateOfResidence: undefined,
    eventZip: '',
    birthGender: '',
    raceEthnicity: undefined,
    householdIncome: '',
    householdSize: '',
    insuranceCoverage: undefined,
    doctorHelpInterest: '',

    // Step 3: Medical History
    heartAttackOrStroke: [], // New field for heart attack/stroke checkboxes
    stressLevel: '', // New field for stress level rating (1-5)
    mentalHealthInterest: '', // New field for mental health services interest (yes/no)
    medicalConditions: [],
    referredToCardiologist: '',
    isParentOrCaregiver: '',
    currentMedications: [],
    physicalActivityFrequency: '',
    resourceAccess: [],
    careAlternative: '',

    // Step 4: Consents & Releases
    mediaRelease: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    endlessHealthAcknowledgment: '',
    dataSharingPermission: '',
    heartGuardChampionOptIn: '',
    bhaWaiverConsent: false,
    transactionalSmsConsent: false,
    marketingSmsConsent: false,
    aiVoiceCallConsent: false,
    
    // Optional Questions (Section 7)
    healthConcern: '',
    healthSupport: '',
    
    // Step 5: Waivers & Opt-ins (kept for backwards compatibility)
    procedureRisksAcknowledgement: false,
    acknowledgeContraindications: [],
    currentDate: moment().format('MM/DD/YYYY')
  });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isPreRegistrationSubmission, setIsPreRegistrationSubmission] = useState(false);

  // Load available bookings for event selection
  const loadEvents = async () => {
    try {
      setLoadingEvents(true);
      const BookingService = await import('../../services/BookingService').then(module => module.default);
      
      // Use the public events endpoint instead of admin bookings
      // This endpoint doesn't require authentication and works for public-facing forms
      const response = await BookingService.getUpcomingPublicEvents();
      
      console.log('Public events API response:', response); // Debug log
      
      if (response.success && response.data && Array.isArray(response.data)) {
        const events = response.data;
        console.log('Available public events:', events); // Debug log
        
        // Transform public events into dropdown options
        const eventOptions = events.map(event => ({
          _id: event._id,
          // Use the event title which includes business/event name
          eventTypeName: event.title,
          bookingDate: event.startDate,
          timeSlot: {
            startTime: event.startDate,
            endTime: event.endDate
          },
          eventType: event.eventType,
          location: event.location,
          businessEventName: event.title,
          // NEW: Track if this is a pre-registration event
          isPreRegistration: event.isPreRegistration || false,
          // Multi-day booking fields
          isMultiDay: event.isMultiDay || false,
          bookingEndDate: event.bookingEndDate || null
        }));
        
        setAvailableEvents(eventOptions);
      } else {
        console.log('No public events found or invalid response:', response);
        setAvailableEvents([]);
      }
    } catch (error) {
      console.error('Error loading public events:', error);
      console.error('Error details:', error.message, error.stack);
      // Fallback: if API fails, show empty array
      setAvailableEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };  // Load events on component mount to have them available for all users
  useEffect(() => {
    loadEvents();
  }, []);

  // Load events when user becomes a returning user (for historical compatibility)
  useEffect(() => {
    if (isReturningUser && !availableEvents.length && !loadingEvents) {
      loadEvents();
    }
  }, [isReturningUser]);

  // Add effect to check patient status when both email and DOB are available
  useEffect(() => {
    if (formData.emailAddress && formData.dateOfBirth) {
      checkPatientExists(formData.emailAddress, formData.dateOfBirth);
    }
  }, [formData.emailAddress, formData.dateOfBirth]);

  // Extract patient ID from URL parameters and load patient context
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get('patientId');
    
    if (patientId) {
      loadPatientContext(patientId);
    }
  }, [location]);

  // Load patient information from the screening entry context
  const loadPatientContext = async (patientId) => {
    try {
      setLoadingPatientContext(true);
      const PatientService = await import('../../services/PatientService').then(module => module.default);
      const response = await PatientService.getPatientDetails(patientId);
      const patient = response.data;
      
      setPatientContext(patient);
      
      // Pre-populate form with patient information if available
      if (patient) {
        setFormData(prev => ({
          ...prev,
          firstName: patient.firstName || '',
          lastName: patient.lastName || '',
          dateOfBirth: patient.dateOfBirth || '',
          emailAddress: patient.email || '',
          phoneNumber: patient.phoneNumber || '',
          // Set as existing user since they're coming from patient queue
          usedBefore: 'yes'
        }));
        
        // Skip to step 2 since we have basic patient info
        setCurrentStep(1);
      }
    } catch (error) {
      console.error('Error loading patient context:', error);
      // Continue with normal flow if patient context fails to load
    } finally {
      setLoadingPatientContext(false);
    }  };

  // Handle completion - navigate back to patient queue and mark as completed
  const handlePatientScreeningComplete = async (formData) => {
    if (patientContext) {
      try {
        const PatientService = await import('../../services/PatientService').then(module => module.default);
        
        // Update patient status to fully completed
        await PatientService.updateQueueStatus(patientContext._id || patientContext.patientId, { 
          status: 'completed',
          completionTime: new Date().toISOString(),
          busScreeningData: formData
        });
        
        // Navigate back to patient queue
        navigate('/admin/patient-queue');
      } catch (error) {
        console.error('Error updating patient status:', error);
        // Fall back to normal submission flow
      }
    }
  };

  // Function to check if patient exists
  const checkPatientExists = async (emailAddress, dateOfBirth) => {
    if (!emailAddress || !dateOfBirth) {
      setPatientCheckStatus(null);
      setExistingPatientInfo(null);
      return;
    }

    try {
      setCheckingPatient(true);
      setPatientCheckStatus('checking');

      const response = await PatientService.checkPatientExists(emailAddress, dateOfBirth);

      if (response.exists) {
        setPatientCheckStatus('found');
        setExistingPatientInfo({
          patientId: response.patientId,
          message: response.message
        });
        setIsReturningUser(true);
      } else if (response.potentialDuplicates > 0) {
        setPatientCheckStatus('duplicates');
        setExistingPatientInfo({
          duplicateCount: response.potentialDuplicates,
          message: response.message
        });
      } else {
        setPatientCheckStatus('not_found');
        setExistingPatientInfo(null);
        setIsReturningUser(false);
      }
    } catch (error) {
      console.error('Error checking patient:', error);
      setPatientCheckStatus(null);
      setExistingPatientInfo(null);
    } finally {
      setCheckingPatient(false);
    }
  };

  // Debounced version of checkPatientExists - waits 800ms after user stops typing
  const debouncedCheckPatientExists = useCallback((emailAddress, dateOfBirth) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Clear previous status immediately when user starts typing
    setPatientCheckStatus(null);
    setExistingPatientInfo(null);
    setCheckingPatient(false);

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      checkPatientExists(emailAddress, dateOfBirth);
    }, 800); // 800ms delay after user stops typing
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  const nextStep = (values) => {
    console.log(`🔄 Step ${currentStep} -> next:`, {
      currentValues: values,
      medicalConditions: values.medicalConditions,
      existingFormData: formData
    });
    
    setFormData(values);

    // Check if user selected "yes" for usedBefore on first step
    if (currentStep === 0 && values.usedBefore === 'yes') {
      setIsReturningUser(true);
    }

    // Skip hidden steps
    let next = currentStep + 1;
    while (next < formSteps.length && !isStepVisible(next)) {
      next++;
    }
    if (next < formSteps.length) {
      setCurrentStep(next);
    }
    
    // Scroll to top of form when moving to next step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = (values) => {
    setFormData(values);

    // Reset returning user state if going back from first step
    if (currentStep === 1 && isReturningUser) {
      setIsReturningUser(false);
    }

    // Skip hidden steps
    let prev = currentStep - 1;
    while (prev >= 0 && !isStepVisible(prev)) {
      prev--;
    }
    if (prev >= 0) {
      setCurrentStep(prev);
    }
    
    // Scroll to top of form when going back to previous step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      const finalValues = { ...formData, ...values };
      setFormData(finalValues);

      // If we have a patient context (coming from screening entry), handle it differently
      if (patientContext) {
        await handlePatientScreeningComplete(finalValues);
        setSubmitSuccess(true);
        return;
      }

      // Original submission logic for normal bus screening flow
      let eventId = finalValues.selectedEvent || null;

      if (!eventId && availableEvents.length > 0) {
        // Use the first available event as default
        eventId = availableEvents[0]._id;
      }

      // Fallback to walk-in sentinel if no event selected
      if (!eventId) {
        eventId = '000000000000000000000000';
      }

      console.log('Using eventId:', eventId);
      
      // Transform medical conditions array to individual boolean fields
      const medicalConditions = finalValues.medicalConditions || [];
      
      const medicalHistory = {
        priorHeartAttack: medicalConditions.includes('heartProblems'),
        priorStroke: medicalConditions.includes('heartProblems'),
        angina: medicalConditions.includes('heartProblems'),
        heartFailure: medicalConditions.includes('heartProblems'),
        atrialFibrillation: medicalConditions.includes('heartProblems'),
        peripheralArteryDisease: medicalConditions.includes('heartProblems'),
        carotidArteryDisease: medicalConditions.includes('heartProblems'),
        diabetes: medicalConditions.includes('diabetes'),
        hypertension: medicalConditions.includes('highBloodPressure'),
        highCholesterol: medicalConditions.includes('badCholesterol'),
        chronicKidneyDisease: medicalConditions.includes('kidneyDisease'),
        thyroidDisease: false,
        familyHistoryHeartDisease: false,
        familyHistoryEarlyHeartDisease: false,
        familyHistoryStroke: false,
        familyHistoryDiabetes: false,
        familyHistoryHighBP: false,
        familyFirstDegreeRelatives: [],
        previousCardiacProcedures: [],
        previousHospitalizations: [],
        lastUpdated: new Date().toISOString()
      };
      
      // Initialize medications object with all false values
      const medications = {
        aceInhibitors: false,
        arbBlockers: false,
        betaBlockers: false,
        calciumChannelBlockers: false,
        diuretics: false,
        statins: false,
        fibrates: false,
        ezetimibe: false,
        pcsK9Inhibitors: false,
        metformin: false,
        insulin: false,
        sulfonylureas: false,
        sglt2Inhibitors: false,
        glp1Agonists: false,
        aspirin: false,
        clopidogrel: false,
        warfarin: false,
        doacs: false,
        nitrates: false,
        digitalis: false,
        antiarrhythmics: false,
        lastUpdated: new Date().toISOString()
      };
      
      // Add patient check status to submission data
      const submissionData = {
        ...finalValues,
        patientCheckStatus: patientCheckStatus,
        existingPatientId: existingPatientInfo?.patientId || null,
        // Map selectedEvent to eventId for backend compatibility
        eventId: eventId,
        // Check if this is a pre-registration submission
        isPreRegistration: availableEvents.find(e => e._id === eventId)?.isPreRegistration || false,
        // Add structured medical history and medications
        medicalHistory: medicalHistory,
        medications: medications,
        // Additional intake questions
        isParentOrCaregiver: finalValues.isParentOrCaregiver || '',
        currentMedications: finalValues.currentMedications || [],
        physicalActivityFrequency: finalValues.physicalActivityFrequency || '',
        resourceAccess: finalValues.resourceAccess || [],
        careAlternative: finalValues.careAlternative || ''
      };

      // Ensure all required consent fields are present
      // These are fields that are required by the backend
      const requiredConsentFields = [
        'bhaWaiverConsent',
        'endlessHealthAcknowledgment',
        'procedureRisksAcknowledgement',
        'dataSharingPermission',
        'heartGuardChampionOptIn',
        'mediaRelease'
      ];

      // Validate consent fields are present
      const missingFields = requiredConsentFields.filter(field => {
        const value = submissionData[field];
        return value === undefined || value === null || value === '';
      });

      if (missingFields.length > 0) {
        console.error('Missing required consent fields:', missingFields);
        // Add default values for missing fields to prevent API errors
        missingFields.forEach(field => {
          // Set reasonable defaults based on field type
          if (field === 'bhaWaiverConsent' || field === 'endlessHealthAcknowledgment' || field === 'procedureRisksAcknowledgement') {
            submissionData[field] = true;
          } else if (field === 'dataSharingPermission' || field === 'heartGuardChampionOptIn' || field === 'mediaRelease') {
            submissionData[field] = 'yes';
          }
        });
      }

      // Ensure currentDate is set for consentDate
      if (!submissionData.currentDate) {
        const today = new Date();
        submissionData.currentDate = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
      }

      // Import and use the ScreeningService to submit the data securely
      const ScreeningService = await import('../../services/ScreeningService').then(module => module.default);
      await ScreeningService.submitScreeningForm(submissionData);

      // Track if this was a pre-registration for success message
      setIsPreRegistrationSubmission(submissionData.isPreRegistration || false);
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('There was an error submitting your information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }  };
    const stepTitles = [
    t('form.steps.patientInfo'),
    t('form.steps.demographics'),
    t('form.steps.medicalHistory'),
    t('form.steps.consents')
  ];// Array of form steps - now using extracted components
  const formSteps = [
    // Step 1: Patient Info (with conditional content for returning users)
    ({ values, errors, touched, setFieldValue }) => (
      <StepPatientInfo
        values={values}
        errors={errors}
        touched={touched}
        setFieldValue={setFieldValue}
        isReturningUser={isReturningUser}
        setIsReturningUser={setIsReturningUser}
        patientCheckStatus={patientCheckStatus}
        setPatientCheckStatus={setPatientCheckStatus}
        existingPatientInfo={existingPatientInfo}
        checkingPatient={checkingPatient}
        availableEvents={availableEvents}
        loadingEvents={loadingEvents}
        debouncedCheckPatientExists={debouncedCheckPatientExists}
        t={t}
        isFieldVisible={isFieldVisible}
        isFieldRequired={isFieldRequired}
        onEventChange={(eventId) => loadConfig(eventId)}
      />
    ),
    // Step 2: Contact & Demographics
    ({ values, errors, touched, setFieldValue }) => (
      <StepDemographics
        values={values}
        errors={errors}
        touched={touched}
        setFieldValue={setFieldValue}
        isFieldVisible={isFieldVisible}
        isFieldRequired={isFieldRequired}
        isSectionVisible={isSectionVisible}
      />
    ),
    // Step 3: Medical History
    ({ values, setFieldValue }) => (
      <StepMedicalHistory
        values={values}
        setFieldValue={setFieldValue}
        isFieldVisible={isFieldVisible}
        isFieldRequired={isFieldRequired}
        isSectionVisible={isSectionVisible}
      />
    ),
    // Step 4: Consents & Releases
    ({ values, errors, touched, setFieldValue }) => (
      <StepConsents
        values={values}
        errors={errors}
        touched={touched}
        setFieldValue={setFieldValue}
        isFieldVisible={isFieldVisible}
        isFieldRequired={isFieldRequired}
        isSectionVisible={isSectionVisible}
      />
    )
  ];

  // Step field mapping for determining if a step has any visible content
  const STEP_FIELDS = [
    [], // Step 0: Patient Info — always visible (locked fields)
    ['phoneNumber', 'birthGender', 'raceEthnicity', 'householdIncome', 'householdSize', 'insuranceCoverage', 'doctorHelpInterest', 'stateOfResidence', 'eventZip'],
    ['heartAttackOrStroke', 'stressLevel', 'mentalHealthInterest', 'isParentOrCaregiver', 'currentMedications', 'physicalActivityFrequency', 'resourceAccess', 'careAlternative', 'medicalConditions', 'referredToCardiologist'],
    ['mediaRelease', 'endlessHealthAcknowledgment', 'dataSharingPermission', 'heartGuardChampionOptIn', 'bhaWaiverConsent', 'transactionalSmsConsent', 'marketingSmsConsent', 'aiVoiceCallConsent', 'healthConcern', 'healthSupport']
  ];

  const isStepVisible = (stepIndex) => {
    if (stepIndex === 0) return true; // Step 0 always visible (locked fields)
    return STEP_FIELDS[stepIndex].some(f => isFieldVisible(f));
  };

  const visibleStepIndices = [0, 1, 2, 3].filter(i => isStepVisible(i));
  const currentVisibleIndex = visibleStepIndices.indexOf(currentStep);
  const isLastVisibleStep = currentStep === visibleStepIndices[visibleStepIndices.length - 1];

  // Get step titles based on user type
  const getStepTitles = () => {
    if (isReturningUser) {
      return ['Patient Information & Consent'];
    }
    return visibleStepIndices.map(i => stepTitles[i]);
  };

  // Get total steps based on user type
  const getTotalSteps = () => {
    return isReturningUser ? 1 : visibleStepIndices.length;
  };

  // Debug logging to check render conditions
  console.log('BusDataFormWizard render state:', {
    submitSuccess,
    submitError,
    loadingPatientContext,
    patientContext,
    currentStep,
    isReturningUser,
    formSteps: formSteps.length
  });

  // Safety check for formSteps
  if (!formSteps || formSteps.length === 0) {
    console.error('FormSteps not initialized properly');
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Loading form components...</h3>
      </div>
    );
  }

  if (submitSuccess) {
    return (      <div style={inlineStyles.successContainer}>
        <WizardContainer style={inlineStyles.spaceVertical}>
          <div style={inlineStyles.successInnerContainer}>            <HeartFilled 
              style={inlineStyles.heartIcon}
            />
            <style>
              {`
                @keyframes heartbeat {
                  0% { 
                    transform: scale(1);
                    opacity: 1;
                  }
                  50% { 
                    transform: scale(1.2);
                    opacity: 0.8;
                  }
                  100% { 
                    transform: scale(1);
                    opacity: 1;
                  }
                }
                ${GlobalButtonStyles}
              `}
            </style>            
              <Title level={3} style={{ color: '#000', marginBottom: '16px' }}>
                {isPreRegistrationSubmission ? 'Pre-Registration Complete!' : 'Thank You!'}
              </Title>            
            <Paragraph style={inlineStyles.submitParagraph}>
              {isPreRegistrationSubmission 
                ? 'You have successfully pre-registered for the health screening event. We appreciate you taking this step to prioritize your heart health!'
                : 'Your information has been successfully submitted. We appreciate your participation in the Black Heart Associations health screening program.'
              }
            </Paragraph>
            <Paragraph style={inlineStyles.submitParagraph}>
              {isPreRegistrationSubmission 
                ? 'On the day of the event, please check in with the event staff. They will have your information ready and you will be moved to the active screening queue.'
                : 'Please wait to be called by the event staff for your screening.'
              }
            </Paragraph>
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            </div>
          </div>
        </WizardContainer>
      </div>
    );
  }

  // Show error message if submission failed
  if (submitError) {
    return (
      <WizardContainer>        <div style={inlineStyles.errorContainer}>
          <Title level={3} style={inlineStyles.errorTitle}>Submission Error</Title>
          <Paragraph style={inlineStyles.submitParagraph}>
            {submitError}
          </Paragraph>
          <Button
            type="primary"
            onClick={() => setSubmitError('')}
            style={inlineStyles.primaryButton}
          >
            Try Again
          </Button>
          <Button
            type="default"
            onClick={() => {
              setSubmitError('');
              setCurrentStep(0);
              setIsReturningUser(false);
            }}
            style={inlineStyles.defaultButton}
          >
            Start Over
          </Button>
        </div>
      </WizardContainer>
    );  }
  // Show loading screen when loading patient context
  if (loadingPatientContext) {
    return (
      <>
        <SharedGlobalSelectStyles />
        <WizardContainer>
          <WizardHeader>
            <MainTitle level={2}>
              Loading Patient Information...
            </MainTitle>
            <Divider style={inlineStyles.headerDivider} />
          </WizardHeader>
          <StepContainer style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <Spin size="large" />
          </StepContainer>
        </WizardContainer>
      </>
    );
  }
  return (
    <>
      <SharedGlobalSelectStyles />
      <style>
        {`
          @keyframes heartbeat {
            0% { 
              transform: scale(1);
              opacity: 1;
            }
            50% { 
              transform: scale(1.2);
              opacity: 0.8;
            }
            100% { 
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes fadeInSlideDown {
            0% {
              opacity: 0;
              transform: translateY(-10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <WizardContainer>
        <WizardHeader>
          <HeaderRow>
            <MainTitle level={2}>
              {t('form.title')}
            </MainTitle>
            <LanguageToggle />
          </HeaderRow>
          {patientContext && (
            <Alert
              message={`Continuing screening for patient: ${patientContext.firstName} ${patientContext.lastName}`}
              type="info"
              showIcon
              style={{ marginTop: 16, marginBottom: 0 }}
            />
          )}
          <Divider style={inlineStyles.headerDivider} />
        </WizardHeader><FormProgress>
          <span className="steps">{t('form.stepOf', { current: currentVisibleIndex + 1, total: getTotalSteps() })}: {getStepTitles()[currentVisibleIndex] || getStepTitles()[0]}</span>
        </FormProgress>

        <StyledSteps current={currentVisibleIndex} size="small">
          {getStepTitles().map((title, index) => (
            <Step key={index} title={title} />
          ))}
        </StyledSteps>

        <StepContainer>        <Formik
          initialValues={formData}
          validationSchema={getValidationSchema(currentStep, isReturningUser, isFieldVisible)}
          validateOnChange={false}
          validateOnBlur={false}
          validateOnMount={false}
          onSubmit={(values, { setTouched, validateForm }) => {
            // Mark all fields as touched to show validation errors
            validateForm().then(errors => {
              if (Object.keys(errors).length > 0) {
                // Mark all fields as touched so errors will display
                const touched = Object.keys(errors).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {});
                setTouched(touched);
              } else {
                // No errors, proceed
                if (isReturningUser && currentStep === 0) {
                  handleSubmit(values);
                } else if (isLastVisibleStep) {
                  handleSubmit(values);
                } else {
                  nextStep(values);
                }
              }
            });
          }}
        >
          {(formikProps) => (
            <Form autoComplete="off">
              <div>
                {formSteps[currentStep](formikProps)}

                <NavigationButtons isLastStep={isReturningUser || isLastVisibleStep}>
                  {currentStep > 0 && (
                    <StyledButton
                      type="default"
                      onClick={() => prevStep(formikProps.values)}
                      icon={<FontAwesomeIcon icon={faArrowLeft} />}
                      disabled={isSubmitting}
                    >
                      {t('navigation.back')}
                    </StyledButton>
                  )}

                  {currentStep === 0 && <div />}
                  <StyledButton
                    type="primary"
                    htmlType="submit"
                    size={(isReturningUser && currentStep === 0) || isLastVisibleStep ? "large" : "middle"}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {((isReturningUser && currentStep === 0) || isLastVisibleStep) ? (
                      <>
                        {isSubmitting ? t('navigation.submitting') : t('navigation.submit')} {!isSubmitting && <CheckOutlined />}
                      </>
                    ) : (
                      <>
                        {t('navigation.next')} <FontAwesomeIcon icon={faArrowRight} />
                      </>
                    )}
                  </StyledButton>
                </NavigationButtons>
              </div>
            </Form>
          )}
        </Formik>
        </StepContainer>
      </WizardContainer>
    </>
  );
};

// Wrap with LanguageProvider for language support
const BusDataFormWizardWithLanguage = () => (
  <LanguageProvider>
    <BusDataFormWizard />
  </LanguageProvider>
);

export default BusDataFormWizardWithLanguage;